import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { handleDomBubbleEvent } from '@/utils/dom';
import GenericMultiselectDropdown from '@/components/ui/multiselect-dropdown/generic-multiselect-dropdown';
import { useUserModalRootActionsStore } from '@/features/users/_shared/components/user-modal-root/user-modal-root.store';
import { GET_LIST_AGENT_TO_TEAM_QUERY_KEY, useGetListAgentToTeam } from '../../../api/get-list-agent-to-team.api';
import { TAgent } from '@/features/users/agent/types/agent.type';
import { AddAgentToTeamSchema, TAddAgentToTeamPayload, useAddAgentToTeam } from '../../../api/add-agent-to-team.api';
import { GET_LIST_TEAM_QUERY_KEY } from '../../../api/get-list-team-api';
import { GET_LIST_AGENT_QUERY_KEY } from '@/features/users/agent/api/get-list-agent-api';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';
import type { AxiosErrorCustom } from '@/lib/axios';

const TOAST_MESSAGES_ADD_AGENT_TO_TEAM = {
  ADD_AGENT_TO_TEAM_ERROR: 'Invite agent to team failed!',
  ADD_AGENT_TO_TEAM_SUCCESS: 'Invite agent to team successfully!',
};

export type TInviteAgentToTeamModalType = 'InviteAgentToTeamModal';
export type IInviteAgentToTeamModalProps = {
  teamId: string;
};

const InviteAgentToTeamModal = memo(({ teamId }: IInviteAgentToTeamModalProps) => {
  const userModalRootActions = useUserModalRootActionsStore();
  const showToast = useCustomToast();

  const isOpenInviteAgentToTeamModal = useMemo(() => {
    return userModalRootActions.checkIsOpenModal('InviteAgentToTeamModal');
  }, [userModalRootActions]);

  /**
   * Get agent to team
   */
  const { data: agentToTeamData, isLoading: isLoadingAgentToTeam } = useGetListAgentToTeam({
    params: {
      teamId: teamId || '',
    },
    queryConfig: {
      enabled: !!teamId,
    },
  });

  const agentToTeamOptions = useMemo(() => {
    return agentToTeamData?.payload || [];
  }, [agentToTeamData]);

  /**
   * Form
   */
  const [selectedAgents, setSelectedAgents] = useState<TAgent[]>([]);

  const form = useForm<TAddAgentToTeamPayload>({
    mode: 'onChange',
    resolver: zodResolver(AddAgentToTeamSchema),
  });

  /**
   * Sync teamId to form from modal store
   */
  useEffect(() => {
    if (teamId) {
      form.reset({
        teamId,
        userIds: [],
      });
    }
  }, [teamId, form]);

  /**
   * Handle change selected agents
   */
  const handleChangeSelectedAgents = useCallback(
    (agents: TAgent[]) => {
      setSelectedAgents(agents);
      form.setValue(
        'userIds',
        agents.map((agent) => agent.userId)
      );
    },
    [form]
  );

  /**
   * Perform mutation invite agent to team
   */
  const mutationInviteAgentToTeam = useAddAgentToTeam({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage =
          axiosExtractErrorMessage(error) || TOAST_MESSAGES_ADD_AGENT_TO_TEAM.ADD_AGENT_TO_TEAM_ERROR;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        const currentTeamId = form.getValues('teamId');
        form.reset();
        setSelectedAgents([]);
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [GET_LIST_TEAM_QUERY_KEY, { teamId: currentTeamId }] }),
          queryClient.invalidateQueries({
            queryKey: [GET_LIST_AGENT_TO_TEAM_QUERY_KEY, { teamId: currentTeamId }],
          }),
          queryClient.invalidateQueries({ queryKey: [GET_LIST_AGENT_QUERY_KEY, { teamId: currentTeamId }] }),
        ]);
        userModalRootActions.closeModal('InviteAgentToTeamModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_ADD_AGENT_TO_TEAM.ADD_AGENT_TO_TEAM_SUCCESS });
      },
    },
  });

  /**
   * Handle submit form
   */
  const onSubmit = useCallback(
    (data: TAddAgentToTeamPayload) => {
      if (data.userIds.length > 0 && data.teamId) {
        mutationInviteAgentToTeam.mutate({
          teamId: data.teamId,
          userIds: data.userIds,
        });
      }
    },
    [mutationInviteAgentToTeam]
  );

  const handleClose = useCallback(() => {
    form.reset();
    setSelectedAgents([]);
    userModalRootActions.closeModal('InviteAgentToTeamModal');
  }, [form, userModalRootActions]);

  return (
    <Modal isOpen={isOpenInviteAgentToTeamModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[400px]">
      <ModalHeader>
        <ModalTitle>Invite Agent</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(onSubmit)(e);
          }}
          id="invite-agent-to-team-form"
          className="grid gap-[16px]"
        >
          <div className="space-y-[4px] w-full">
            <label className="text-[14px] block font-normal text-[#9D9D9C]">Select Agents*</label>
            <GenericMultiselectDropdown
              isLoading={isLoadingAgentToTeam}
              options={agentToTeamOptions}
              value={selectedAgents}
              onChange={handleChangeSelectedAgents}
              labelKey="fullname"
              valueKey="userId"
              placeholder="Select Agents"
              searchPlaceholder="Search agents..."
            />
            {form?.formState?.errors?.userIds && (
              <span className="text-[14px] font-normal text-[#FF0000]">{form.formState.errors.userIds.message}</span>
            )}
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton
          form="invite-agent-to-team-form"
          type="submit"
          disabled={mutationInviteAgentToTeam.isPending}
        >
          {mutationInviteAgentToTeam.isPending ? <Spinner className="w-[18px] h-[18px]" /> : null} Invite
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

InviteAgentToTeamModal.displayName = 'InviteAgentToTeamModal';
export default InviteAgentToTeamModal;
